export interface PinDetails {
  code?: string;
  serial?: string;
  info1?: string | string[];
  info2?: string | string[];
  info3?: string | string[];
  [key: string]: unknown;
}

export interface TopUp {
  tuId: string;
  name: string;
  provider: string;
  operatorId: number;
  amount: string;
  deliveredAmount: string;
  fee: string;
  discount: string;
  currency: string;
  deliveredAmountCurrency: string;
  countryCode: string;
  phoneNumber: string;
  logoUrl: string;
  pin: boolean;
  pinDetails: PinDetails | null;
  operatorTransactionId: string | null;
  applicationId: string;
  createdAt: string;
  updatedAt: string;
  denomination: string;
  transactionId: string;
  hasFailedOrder: boolean;
}

export function isOngoing(topUp: TopUp): boolean {
  return (
    !topUp.pinDetails?.code &&
    (topUp.hasFailedOrder ||
      !topUp.transactionId ||
      !topUp.operatorTransactionId)
  );
}

function createdAtMs(topUp: TopUp): number {
  const time = new Date(topUp.createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * GD-125 — a top-up placed in the last 15 minutes that is still Ongoing: the
 * member was most likely sent here straight after paying and is waiting for
 * it. An older Ongoing row may never change (an orphaned failover row, see
 * `latestTopUpPerApplication`), so it doesn't count.
 */
export function awaitsDelivery(topUps: TopUp[], now = Date.now()): boolean {
  return topUps.some(
    (topUp) => isOngoing(topUp) && now - createdAtMs(topUp) < 15 * 60 * 1000,
  );
}

/**
 * One purchase can leave more than one top-up row. When the Reloadly leg fails,
 * the API places a DT One order and inserts a *new* row against the same
 * applicationId instead of updating the original (api/src/utils/orderTopUp.ts).
 * The superseded row never receives a PIN or an operatorTransactionId, so
 * isOngoing() pins it to "Ongoing" indefinitely and the customer sees the same
 * purchase twice — once delivered, once apparently stuck.
 *
 * Until the API stops orphaning rows, keep only the most recent attempt per
 * application. Rows carrying no applicationId are never collapsed together.
 */
export function latestTopUpPerApplication(topUps: TopUp[]): TopUp[] {
  const latest = new Map<string, TopUp>();

  for (const topUp of topUps) {
    const key = topUp.applicationId || topUp.tuId;
    const current = latest.get(key);

    if (!current || createdAtMs(topUp) > createdAtMs(current)) {
      latest.set(key, topUp);
    }
  }

  return [...latest.values()];
}
